import { useEffect, useMemo, useState } from "react";
import { API, api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "./Dashboard";
import { FileDown, MailPlus, Send } from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { EditorialList, EditorialRow, EditorialAction } from "@/components/admin/EditorialList";
import EditorialEmptyState from "@/components/editorial/EditorialEmptyState";
import useLottieOnce from "@/hooks/useLottieOnce";
import quillSignatureLottie from "@/assets/lottie/quill-signature.json";
import quillFlourishLottie from "@/assets/lottie/quill-flourish.json";
import { EDITORIAL_ASSETS } from "@/lib/editorialAssets";
import EditorialDialog, { EditorialField } from "@/components/admin/EditorialDialog";
import EditorialSelect from "@/components/admin/EditorialSelect";

const EMPTY_DRAFT = { booking_ids: [], customer_name: "", customer_email: "", items: [{ label: "", amount: "" }], amount: "", deposit_paid: "", due_date: "", notes: "" };

/* Give new invoice drafts a default due date of "today + 14 days" so the
   date field ships pre-filled instead of showing an empty `mm/dd/yyyy`
   placeholder that reads as "no picker available". Studios can still
   change it, but the sensible default matches typical Net-14 invoicing. */
function defaultDueDate() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
}

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [mode, setMode] = useState("bookings"); // "bookings" | "manual"
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT, due_date: defaultDueDate() });
  const [fireSend, sendSurface] = useLottieOnce(quillFlourishLottie, { size: 96 });

  async function load() {
    const [inv, bk] = await Promise.all([api.get("/admin/invoices"), api.get("/admin/bookings?status_filter=all")]);
    setInvoices(inv.data);
    setBookings(bk.data);
  }
  useEffect(() => { load(); }, []);

  // Dirty when the studio has picked at least one booking (bookings mode) or
  // typed a client name (manual mode).
  const dirty = useMemo(
    () => (mode === "bookings" ? draft.booking_ids.length > 0 : Boolean(draft.customer_name)),
    [mode, draft.booking_ids, draft.customer_name],
  );

  function toggleBooking(bookingId) {
    setDraft((d) => {
      const on = d.booking_ids.includes(bookingId);
      const nextIds = on ? d.booking_ids.filter((x) => x !== bookingId) : [...d.booking_ids, bookingId];
      // Recompute amount/deposit from the selected sessions so the studio
      // sees real numbers, still editable afterwards.
      const sel = bookings.filter((b) => nextIds.includes(b.id));
      const amount = sel.reduce((s, b) => s + (Number(b.tier_price) || 0), 0);
      const deposit = sel.reduce((s, b) => s + (b.deposit_received ? Number(b.deposit_amount) || 0 : 0), 0);
      return { ...d, booking_ids: nextIds, amount: nextIds.length ? String(amount) : "", deposit_paid: nextIds.length && deposit ? String(deposit) : "" };
    });
  }

  async function downloadPdf(inv) {
    const filename = `${inv.invoice_number}.pdf`;
    const tid = toast.loading("Preparing PDF\u2026");
    try {
      // Mobile (iOS/Android): hand the actual PDF FILE to the native share
      // sheet — "Save to Files", AirDrop, email. No blob URL, no site link,
      // and it works inside the home-screen app where downloads have no UI.
      const probe = new File([new Blob()], filename, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [probe] })) {
        const res = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: "blob" });
        const file = new File([res.data], filename, { type: "application/pdf" });
        toast.dismiss(tid);
        try {
          await navigator.share({ files: [file], title: filename });
          toast.success("PDF ready");
        } catch (err) {
          if (err?.name !== "AbortError") toast.error("Could not open the share sheet");
        }
        return;
      }
      // Desktop: short-lived token → plain URL navigation. The browser saves
      // the real PDF file natively (attachment header), no blob artifact.
      const res = await api.post(`/admin/invoices/${inv.id}/download-token`);
      const a = document.createElement("a");
      a.href = `${API}/invoices/${inv.id}/download?token=${encodeURIComponent(res.data.token)}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.dismiss(tid);
      toast.success(`Downloading ${filename}`);
    } catch {
      toast.dismiss(tid);
      toast.error("Could not generate the PDF");
    }
  }

  function setItem(idx, patch) {
    setDraft((d) => {
      const items = d.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
      const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
      return { ...d, items, amount: total ? String(total) : d.amount };
    });
  }

  function openEdit(inv) {
    setEditId(inv.id);
    setMode("manual");
    setDraft({
      booking_ids: [],
      customer_name: inv.customer_name || "",
      customer_email: inv.customer_email || "",
      items: (inv.line_items && inv.line_items.length)
        ? inv.line_items.map((it) => ({ label: it.label, amount: String(it.amount) }))
        : [{ label: inv.service_name || "", amount: String(inv.amount) }],
      amount: String(inv.amount ?? ""),
      deposit_paid: inv.deposit_paid ? String(inv.deposit_paid) : "",
      due_date: inv.due_date || "",
      notes: inv.notes || "",
    });
    setOpen(true);
  }

  async function create() {
    if (editId) {
      const items = draft.items.filter((it) => it.label.trim());
      try {
        await api.patch(`/admin/invoices/${editId}`, {
          customer_name: draft.customer_name,
          customer_email: draft.customer_email,
          line_items: items.map((it) => ({ label: it.label.trim(), amount: Number(it.amount) || 0 })),
          amount: draft.amount ? Number(draft.amount) : undefined,
          deposit_paid: draft.deposit_paid ? Number(draft.deposit_paid) : 0,
          due_date: draft.due_date || undefined,
          notes: draft.notes,
        });
        setOpen(false);
        setEditId(null);
        setDraft({ ...EMPTY_DRAFT, due_date: defaultDueDate() });
        toast.success("Invoice updated");
        load();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
      return;
    }
    if (mode === "bookings" && draft.booking_ids.length === 0) return toast.error("Select at least one booking");
    if (mode === "manual" && (!draft.customer_name || !draft.customer_email)) return toast.error("Client name and email are required");
    const manualItems = draft.items.filter((it) => it.label.trim());
    if (mode === "manual" && manualItems.length === 0 && !draft.amount) return toast.error("Add at least one session with a price");
    try {
      const payload = {
        booking_ids: mode === "bookings" ? draft.booking_ids : [],
        customer_name: mode === "manual" ? draft.customer_name : undefined,
        customer_email: mode === "manual" ? draft.customer_email : undefined,
        line_items: mode === "manual" ? manualItems.map((it) => ({ label: it.label.trim(), amount: Number(it.amount) || 0 })) : undefined,
        amount: draft.amount ? Number(draft.amount) : undefined,
        deposit_paid: draft.deposit_paid ? Number(draft.deposit_paid) : 0,
        due_date: draft.due_date || undefined,
        notes: draft.notes,
      };
      await api.post("/admin/invoices", payload);
      setOpen(false);
      setDraft({ ...EMPTY_DRAFT, due_date: defaultDueDate() });
      toast.success("Invoice created");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function send(id) {
    try {
      await api.post(`/admin/invoices/${id}/send`);
      toast.success("Invoice emailed");
      fireSend();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function setStatus(id, status) {
    try {
      await api.patch(`/admin/invoices/${id}?status_value=${status}`);
      toast.success("Invoice updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-8" data-testid="admin-invoices-page">
      <AdminPageHeader
        breadcrumb={["WORKFLOW", "INVOICES"]}
        title="Billing & invoices"
        count={invoices.length}
        rightSlot={
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90"
            data-testid="open-create-invoice"
          >
            <MailPlus className="w-4 h-4" /> New invoice
          </button>
        }
      />

      <EditorialDialog
        open={open}
        onClose={() => { setOpen(false); setEditId(null); setDraft({ ...EMPTY_DRAFT, due_date: defaultDueDate() }); }}
        breadcrumb={editId ? "INVOICES · EDIT" : "INVOICES · NEW"}
        title={editId ? "Edit invoice" : "New invoice"}
        subtitle={editId ? "Update client details, sessions and amounts — the PDF and email use the new values." : "Draft an invoice for a client. Save adds it to drafts; you can email it from the row's actions."}
        dirty={dirty}
        onDiscard={() => setDraft({ ...EMPTY_DRAFT, due_date: defaultDueDate() })}
        onSave={create}
        saveLabel="SAVE INVOICE →"
        size="md"
      >
        <div className="space-y-7">
          {/* Mode toggle — hidden while editing (edit always shows the fields) */}
          {!editId && (
          <div className="inline-flex border border-border" role="tablist">
            {[["bookings", "From bookings"], ["manual", "Manual client"]].map(([m, label]) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-4 h-9 text-[10px] uppercase tracking-[0.22em] transition-colors ${mode === m ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`invoice-mode-${m}`}
              >
                {label}
              </button>
            ))}
          </div>
          )}

          {mode === "bookings" ? (
            <EditorialField label={`Sessions (${draft.booking_ids.length} selected)`} required hint="Tick every session this invoice covers.">
              <div className="max-h-56 overflow-y-auto border border-border divide-y divide-border" data-testid="invoice-booking-multiselect">
                {bookings.length === 0 && (
                  <div className="px-3 py-4 text-xs text-muted-foreground">No bookings yet — use Manual client instead.</div>
                )}
                {bookings.map((b) => {
                  const on = draft.booking_ids.includes(b.id);
                  return (
                    <label key={b.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${on ? "bg-[color:var(--gold-base)]/10" : "hover:bg-muted/50"}`}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleBooking(b.id)}
                        className="accent-[color:var(--gold-base)]"
                        data-testid={`invoice-booking-check-${b.id}`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm truncate">{b.customer_name} · {b.service_name}</span>
                        <span className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{b.reference} {b.tier_price != null ? `· $${b.tier_price}` : ""}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </EditorialField>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-6">
                <EditorialField label="Client name" required>
                  <input value={draft.customer_name} onChange={(e) => setDraft({ ...draft, customer_name: e.target.value })} className="input" placeholder="Ada Obi" data-testid="invoice-client-name" />
                </EditorialField>
                <EditorialField label="Client email" required>
                  <input type="email" value={draft.customer_email} onChange={(e) => setDraft({ ...draft, customer_email: e.target.value })} className="input" placeholder="ada@email.com" data-testid="invoice-client-email" />
                </EditorialField>
              </div>
              <EditorialField label="Sessions & prices" required hint="One line per session or extra — the amount below totals automatically.">
                <div className="space-y-2" data-testid="manual-items">
                  {draft.items.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        value={it.label}
                        onChange={(e) => setItem(idx, { label: e.target.value })}
                        className="input flex-1 min-w-0"
                        placeholder="e.g. Birthday shoot"
                        data-testid={`manual-item-label-${idx}`}
                      />
                      <input
                        type="number"
                        value={it.amount}
                        onChange={(e) => setItem(idx, { amount: e.target.value })}
                        className="input"
                        style={{ flex: "0 0 7rem", width: "7rem" }}
                        placeholder="Price"
                        data-testid={`manual-item-amount-${idx}`}
                      />
                      {draft.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setDraft((d) => {
                            const items = d.items.filter((_, i) => i !== idx);
                            const total = items.reduce((s, x) => s + (Number(x.amount) || 0), 0);
                            return { ...d, items, amount: total ? String(total) : "" };
                          })}
                          className="text-muted-foreground hover:text-destructive text-lg leading-none px-1"
                          data-testid={`manual-item-remove-${idx}`}
                          aria-label="Remove line"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, items: [...d.items, { label: "", amount: "" }] }))}
                    className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground underline underline-offset-4"
                    data-testid="manual-item-add"
                  >
                    + Add line
                  </button>
                </div>
              </EditorialField>
            </>
          )}
          <div className="grid grid-cols-2 gap-6">
            <EditorialField label="Amount (USD)">
              <input
                type="number"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                data-testid="invoice-amount"
                className="input"
                placeholder="0"
              />
            </EditorialField>
            <EditorialField label="Deposit paid">
              <input
                type="number"
                value={draft.deposit_paid}
                onChange={(e) => setDraft({ ...draft, deposit_paid: e.target.value })}
                data-testid="invoice-deposit-paid"
                className="input"
                placeholder="0"
              />
            </EditorialField>
          </div>
          <EditorialField label="Due date" hint="Click to open the calendar. Defaults to 14 days out.">
            <input
              type="date"
              value={draft.due_date}
              onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
              data-testid="invoice-due"
              className="input"
            />
          </EditorialField>
          <EditorialField label="Notes">
            <textarea
              rows={3}
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              data-testid="invoice-notes"
              className="input"
              placeholder="Optional note for the client…"
            />
          </EditorialField>
          {/* Hidden submit shim so tests targeting the legacy create button still work. */}
          <button onClick={create} className="hidden" data-testid="invoice-create-btn" aria-hidden tabIndex={-1}>Create</button>
        </div>
      </EditorialDialog>

      {invoices.length === 0 ? (
        <EditorialEmptyState
          eyebrow="The ledger is open"
          headline={<>No invoices <em>yet</em>.</>}
          description="Draft your first invoice from any booking — clients receive a hand-set email with a single click."
          tone="charcoal"
          backdrop={EDITORIAL_ASSETS.editorial_fabric_motion.src}
          lottie={quillSignatureLottie}
          data-testid="invoices-empty"
        />
      ) : (
        <EditorialList data-testid="invoices-rows">
          {/* Send-success burst surface — absolutely positioned overlay anchored
              to the list block. Plays a one-shot quill flourish on api/invoices/:id/send. */}
          <div className="relative">{sendSurface}</div>
          {invoices.map((i, idx) => (
            <EditorialRow
              key={i.id}
              prefix={i.invoice_number}
              title={i.customer_name}
              meta={[
                i.customer_email,
                `${i.service_name} · ${i.tier_name}`,
                `$${i.amount.toFixed(0)} · balance $${i.balance_due.toFixed(0)}`,
              ]}
              status={<StatusBadge value={i.status} />}
              actions={
                <>
                  <EditorialAction
                    onClick={() => openEdit(i)}
                    data-testid={`edit-invoice-${i.invoice_number}`}
                  >
                    Edit
                  </EditorialAction>
                  <button
                    type="button"
                    onClick={() => downloadPdf(i)}
                    data-testid={`download-invoice-${i.invoice_number}`}
                    className="inline-flex items-center gap-1.5 px-3 h-8 border border-border hover:border-foreground/60 text-[10px] uppercase tracking-[0.28em] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <FileDown className="w-3 h-3" />
                    PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => send(i.id)}
                    data-testid={`send-invoice-${i.invoice_number}`}
                    className="inline-flex items-center gap-1.5 px-3 h-8 border border-[color:var(--gold-base)]/60 hover:border-[color:var(--gold-base)] hover:bg-[color:var(--gold-base)]/10 text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-base)] transition-colors"
                  >
                    <Send className="w-3 h-3" />
                    Send invoice
                  </button>
                  {i.status !== "paid" && (
                    <EditorialAction
                      onClick={() => setStatus(i.id, "paid")}
                      data-testid={`mark-paid-${i.invoice_number}`}
                    >
                      Mark paid
                    </EditorialAction>
                  )}
                </>
              }
              data-testid={`invoice-row-${i.invoice_number}`}
            />
          ))}
        </EditorialList>
      )}
    </div>
  );
}

function Input() { return null; }
